import { useCallback, useEffect, useRef } from 'react';
import { useTrackingSummaryWebSocket } from './useTrackingSummaryWebSocket';

const DEBOUNCE_MS = 1500;
// An import or a bulk rate update saves row by row and so sends one event per
// entry; cap the wait so a long stream still refreshes on time.
const MAX_WAIT_MS = 5000;
const TIME_ENTRY_KINDS = new Set(['created', 'updated', 'deleted', 'bulk']);

/**
 * Live refresh for the Time Entries list: another user's create / edit /
 * delete (the backend's TimeEntry post_save / post_delete broadcast on the
 * tracking-summary socket) triggers ONE background refetch.
 *
 * Same guards as useInvoicingLiveRefresh:
 *   1. kind      -- time-entry events only (invoicing, clients/projects and
 *                   email-draft events on the same socket are ignored)
 *   2. teamspace -- the socket carries every teamspace the user belongs to;
 *                   only events for `teamspaceId` count (events without a
 *                   teamspace_id, e.g. `bulk`, are taken as relevant)
 *   3. suspended -- while an add/edit/batch/delete dialog is open the refresh
 *                   is queued, so it never changes rows under an open form
 *   4. debounce  -- a burst collapses into one refetch
 *
 * No echo suppression: the acting tab refetches too (one cheap round trip),
 * which also picks up server-side effects its local patch didn't model.
 *
 * `allTeamspaces` skips guard 2 for pages that show entries from every
 * teamspace the user belongs to (the Calendar); `teamspaceId` is then only
 * used to open the socket.
 */
export const useTimeEntriesLiveRefresh = ({ teamspaceId, suspended = false, onRefresh, allTeamspaces = false }) => {
  const pendingRef = useRef(false);
  const timerRef = useRef(null);
  const firstEventRef = useRef(0);
  const suspendedRef = useRef(suspended);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const flush = useCallback(() => {
    timerRef.current = null;
    firstEventRef.current = 0;
    if (suspendedRef.current || !pendingRef.current) return; // stays queued while suspended
    pendingRef.current = false;
    onRefreshRef.current?.();
  }, []);

  const schedule = useCallback(() => {
    const now = Date.now();
    if (!firstEventRef.current) firstEventRef.current = now;
    const remainingCap = MAX_WAIT_MS - (now - firstEventRef.current);
    const delay = Math.max(0, Math.min(DEBOUNCE_MS, remainingCap));
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, delay);
  }, [flush]);

  // Once every dialog is closed, run the refresh that queued up meanwhile.
  useEffect(() => {
    suspendedRef.current = suspended;
    if (!suspended && pendingRef.current) schedule();
  }, [suspended, schedule]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // Stable identity: the socket hook reconnects whenever onUpdate changes.
  const onUpdate = useCallback((kind, data) => {
    if (!TIME_ENTRY_KINDS.has(kind)) return;
    if (!allTeamspaces && data?.teamspace_id != null && String(data.teamspace_id) !== String(teamspaceId)) return;
    pendingRef.current = true;
    schedule();
  }, [teamspaceId, allTeamspaces, schedule]);

  useTrackingSummaryWebSocket(teamspaceId, onUpdate);
};
